/**
 * `payrollRuns.*` — the monthly Payroll Run (Payroll Increment 2).
 *
 * "Record, don't compute": deductions (tds/pf/esi/pt/other) are TYPED per
 * line; Flint computes only `net = gross − Σ deductions`. No statutory
 * auto-calc, no 24Q / Form 16.
 * @see docs/product/payroll-v1-build-plan.md §"Inc 2 — Payroll run"
 *
 * Lifecycle: create (draft, one line per ACTIVE employee, prefilled from the
 * master) → updateLine (deductions, draft only) → submit (≥ approval threshold
 * → founder approval, else straight to `approved`) → pay (one cash_movement
 * per line, kind='salary'). Append-only: a correction after pay is a NEW run.
 *
 * Money is stringified integer paise on the wire (`moneyPaise`); list uses
 * cursor pagination (AGENTS §7 rule 14).
 */
import { z } from "zod";
import { uuid, paged, moneyPaise, isoDate } from "../_common.js";

/** draft → approved → paid; void is the escape hatch. */
export const payrollRunStatus = z.enum(["draft", "approved", "paid", "void"]);

/** First-of-month period anchor (YYYY-MM-01). */
const periodMonth = isoDate.refine((d) => d.endsWith("-01"), {
  message: "period must be the first of a month (YYYY-MM-01)",
});

// ── create ────────────────────────────────────────────────────────
export const createInput = z.object({
  periodMonth,
  /** Optional — the account net salary is paid from (can be set at pay time). */
  payAccountId: uuid.optional(),
});
export const createOutput = z.object({ id: uuid });

// ── get one (run + lines) ─────────────────────────────────────────
export const getInput = z.object({ id: uuid });

/** One payslip line as returned to the SPA. */
export const runLine = z.object({
  id: uuid,
  employeeId: uuid,
  employeeName: z.string(),
  designation: z.string().nullable(),
  /** Resolved public URL for the employee photo; null = initials avatar. */
  photoUrl: z.string().nullable(),
  grossPaise: moneyPaise,
  tdsPaise: moneyPaise,
  pfPaise: moneyPaise,
  esiPaise: moneyPaise,
  ptPaise: moneyPaise,
  otherDeductionPaise: moneyPaise,
  netPaise: moneyPaise,
  note: z.string().nullable(),
});

export const runDetail = z.object({
  id: uuid,
  periodMonth: isoDate,
  status: payrollRunStatus,
  payAccountId: uuid.nullable(),
  payAccountName: z.string().nullable(),
  totalGrossPaise: moneyPaise,
  totalDeductionsPaise: moneyPaise,
  totalNetPaise: moneyPaise,
  employeeCount: z.number().int().nonnegative(),
  approvedAt: z.string().datetime().nullable(),
  paidAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  /** Tenant approval gate (companies.approval_threshold_paise) — drives the SPA hint. */
  approvalThresholdPaise: moneyPaise,
  /** A pending `payroll_run_above_threshold` approval, if one is open. */
  pendingApprovalId: uuid.nullable(),
  lines: z.array(runLine),
});
export const getOutput = runDetail;

// ── list (cursor pagination, period desc) ─────────────────────────
export const listInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
});

export const listRow = z.object({
  id: uuid,
  periodMonth: isoDate,
  status: payrollRunStatus,
  employeeCount: z.number().int().nonnegative(),
  totalGrossPaise: moneyPaise,
  totalDeductionsPaise: moneyPaise,
  totalNetPaise: moneyPaise,
  paidAt: z.string().datetime().nullable(),
});

/** KPIs for the runs-list tile strip + the "next run" anchor. */
export const listKpis = z.object({
  /** Σ net paid across runs in the current Indian FY. */
  paidThisFyPaise: moneyPaise,
  /** Mean net across paid runs in the current FY (0 when none). */
  avgMonthlyNetPaise: moneyPaise,
  /** Active employees on payroll right now. */
  onPayroll: z.number().int().nonnegative(),
  /** First-of-month for the next run with no non-void run yet (YYYY-MM-01). */
  nextRunPeriod: isoDate,
  /** True iff a non-void run already exists for `nextRunPeriod`'s previous candidate. */
  currentPeriodHasRun: z.boolean(),
});

export const listOutput = paged(listRow).extend({ kpis: listKpis });

// ── update a line (deductions; draft only) ────────────────────────
export const updateLineInput = z.object({
  runId: uuid,
  lineId: uuid,
  /** Each optional — only the supplied deductions change; net recomputes. */
  grossPaise: moneyPaise.optional(),
  tdsPaise: moneyPaise.optional(),
  pfPaise: moneyPaise.optional(),
  esiPaise: moneyPaise.optional(),
  ptPaise: moneyPaise.optional(),
  otherDeductionPaise: moneyPaise.optional(),
  note: z.string().trim().max(255).optional(),
});
export const updateLineOutput = z.object({
  lineId: uuid,
  netPaise: moneyPaise,
  totalNetPaise: moneyPaise,
  totalDeductionsPaise: moneyPaise,
  totalGrossPaise: moneyPaise,
});

// ── submit (draft → approved | awaiting_approval) ─────────────────
export const submitInput = z.object({
  runId: uuid,
  /** Optional — set the pay account at submit time. */
  payAccountId: uuid.optional(),
  /** Resubmit after founder approval (mirrors vendor-payments). */
  approvalId: uuid.optional(),
});
export const submitOutput = z.object({
  id: uuid,
  status: payrollRunStatus,
  /** 'approved' when below threshold or approval consumed; 'awaiting_approval' otherwise. */
  outcome: z.enum(["approved", "awaiting_approval"]),
  /** Set when outcome='awaiting_approval' — the pending approval row. */
  approvalId: uuid.nullable(),
});

// ── pay (approved → paid; one cash_movement per line) ─────────────
export const payInput = z.object({
  runId: uuid,
  payAccountId: uuid,
});
export const payOutput = z.object({
  id: uuid,
  status: payrollRunStatus,
  /** Count of per-employee salary cash_movements posted. */
  movementsPosted: z.number().int().nonnegative(),
  totalNetPaise: moneyPaise,
});

// ── void (draft|approved → void) ──────────────────────────────────
export const voidInput = z.object({
  runId: uuid,
  reason: z.string().trim().min(1).max(255),
});
export const voidOutput = z.object({ id: uuid, status: payrollRunStatus });

// ── derived TS types ──────────────────────────────────────────────
export type PayrollRunStatus = z.infer<typeof payrollRunStatus>;
export type CreateInput = z.infer<typeof createInput>;
export type CreateOutput = z.infer<typeof createOutput>;
export type GetInput = z.infer<typeof getInput>;
export type GetOutput = z.infer<typeof getOutput>;
export type RunDetail = z.infer<typeof runDetail>;
export type RunLine = z.infer<typeof runLine>;
export type ListInput = z.infer<typeof listInput>;
export type ListOutput = z.infer<typeof listOutput>;
export type ListRow = z.infer<typeof listRow>;
export type ListKpis = z.infer<typeof listKpis>;
export type UpdateLineInput = z.infer<typeof updateLineInput>;
export type UpdateLineOutput = z.infer<typeof updateLineOutput>;
export type SubmitInput = z.infer<typeof submitInput>;
export type SubmitOutput = z.infer<typeof submitOutput>;
export type PayInput = z.infer<typeof payInput>;
export type PayOutput = z.infer<typeof payOutput>;
export type VoidInput = z.infer<typeof voidInput>;
export type VoidOutput = z.infer<typeof voidOutput>;
